import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ResourceService } from './resource.service';

import * as ResourceActions from './resource.actions';
import { Action } from '@ngrx/store';
import {
  catchError,
  filter,
  interval,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ResourceUploadStatus } from './models';

const RESOURCE_STATUS_POLL_INTERVAL_MS = 5000;

@Injectable()
export class ResourceEffects {
  private actions$ = inject(Actions);
  private resourceService = inject(ResourceService);


  fetchAndLoadByCourseId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.fetchResourceItemsByCourse.type),
      switchMap((action: any) => {
        return this.resourceService.getAllByCourseId(action.courseId).pipe(
          map((response) => {
            return ResourceActions.fetchResourceItemsSuccess({
              resourceResponse: response,
            });
          }),
          catchError((err) => {
            return of(ResourceActions.fetchResourceItemsFailed({ error: err }));
          })
        );
      })
    )
  );

  pollResourcesByCourseId = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.pollResourceItemsByCourse.type),
      switchMap((action: any) =>
        // default set to 50 secs
        interval(30000).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ResourceActions.stopPollingResourceItem.type)
            )
          ),
          map(() => action)
        )
      ),
      switchMap((action: any) => {
        return this.resourceService.getAllByCourseId(action.courseId).pipe(
          map((response) => {
            return ResourceActions.fetchResourceItemsSuccess({
              resourceResponse: response,
            });
          }),
          catchError((err) => {
            return of(ResourceActions.fetchResourceItemsFailed({ error: err }));
          })
        );
      })
    )
  );

  uploadUrlResource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.uploadUrlResource),
      mergeMap((action) =>
        this.resourceService
          .uploadUrl({
            name: action.name,
            url: action.url,
            type: action.resourceType,
            courseId: action.courseId,
          })
          .pipe(
            mergeMap((response) => [
              ResourceActions.uploadUrlResourceSuccess({
                resourceId: response.resourceId,
              }),
              ResourceActions.pollSingleResourceStatus({
                resourceId: response.resourceId,
              }),
            ]),
            catchError((err) =>
              of(ResourceActions.uploadUrlResourceFailed({ error: err }))
            )
          )
      )
    )
  );

  pollSingleResource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.pollSingleResourceStatus),
      mergeMap((action) =>
        interval(RESOURCE_STATUS_POLL_INTERVAL_MS).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ResourceActions.stopPollingSingleResource),
              filter((stop) => stop.resourceId === action.resourceId)
            )
          ),
          switchMap(() =>
            this.resourceService.getById(action.resourceId).pipe(
              mergeMap((resource) => {
                const isTerminal =
                  resource.uploadStatus === ResourceUploadStatus.COMPLETED ||
                  resource.uploadStatus === ResourceUploadStatus.FAILED;

                const resultActions: Action[] = [
                  ResourceActions.singleResourceStatusUpdated({ resource }),
                ];

                if (isTerminal) {
                  resultActions.push(
                    ResourceActions.stopPollingSingleResource({
                      resourceId: action.resourceId,
                    })
                  );
                }

                return resultActions;
              }),
              catchError(() => of())
            )
          )
        )
      )
    )
  );
}
